import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import ActivateForm from './ActivateForm';
import { safeParseJson, extractApiError } from './passwordValidation';

const ActivatePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const validateToken = useCallback(async (token: string) => {
    setValidatingToken(true);

    try {
      const response = await fetch(
        `${BUILD_VARS.QUERY_DOMAIN}/v1.0/auth/activate/${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const data = await safeParseJson(response);

      if (data?.success) {
        return {
          isValid: true,
          email: data?.data.response?.email,
          userId: data?.data.response?.userId,
          stateToken: data?.data.response?.stateToken,
        };
      } else {
        return {
          isValid: false,
          error: data?.error || 'Invalid activation token',
        };
      }
    } catch (err) {
      return {
        isValid: false,
        error: 'Network error. Please check your connection and try again.',
      };
    } finally {
      setValidatingToken(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: {
      token: string;
      password: string;
      firstName?: string;
      lastName?: string;
      stateToken?: string;
    }) => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const response = await fetch(`${BUILD_VARS.QUERY_DOMAIN}/v1.0/auth/activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (response.ok) {
          const data = await safeParseJson(response);
          const successMessage =
            data?.message || 'Account activated successfully! You can now log in.';
          setSuccess(successMessage);
          message.success('Account activated successfully!');
          const url = data?.data?.postActivation?.redirectUrl;
          if (typeof url === 'string' && url.trim()) {
            window.location.href = url.trim();
          } else {
            navigate('/login');
          }
        } else {
          const errorMessage = await extractApiError(
            response,
            'Failed to activate account. Please try again.',
          );
          setError(errorMessage);
          message.error(errorMessage);
        }
      } catch (err) {
        const errorMessage = 'Network error. Please check your connection and try again.';
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return (
    <ActivateForm
      onSubmit={handleSubmit}
      onValidateToken={validateToken}
      loading={loading}
      validatingToken={validatingToken}
      error={error}
      success={success}
    />
  );
};

export default ActivatePage;
